import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import { useBookings } from '../../hooks/venue/useBookings';
import { VenueBooking } from '../../types/venue';
import BookingCard from '../../components/booking/BookingCard';
import { Calendar, X, AlertCircle } from 'lucide-react';

type TabType = 'upcoming' | 'past' | 'cancelled';

const MyBookingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { bookings, loading, error, fetchMyBookings, cancelBooking } = useBookings();
  const [activeTab, setActiveTab] = useState<TabType>('upcoming');
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    fetchMyBookings();
  }, [fetchMyBookings]);

  const handleCancelClick = (bookingId: string) => {
    setCancellingId(bookingId);
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancellingId) return;

    const result = await cancelBooking(cancellingId);
    if (result.success) {
      setShowCancelModal(false);
      setCancellingId(null);
    }
  };

  const handleCreateMatch = (booking: VenueBooking) => {
    // Store booking data in localStorage to pre-fill create match form
    const matchData = {
      location: booking.venue?.address || '',
      mapsUrl: booking.venue?.mapsUrl || '',
      date: booking.bookingDate,
      time: booking.startTime,
      price: booking.totalAmount,
      venueBookingId: booking.id,
    };
    localStorage.setItem('createMatchFromBooking', JSON.stringify(matchData));
    navigate('/create-match');
  };

  const filterBookings = (bookings: VenueBooking[], tab: TabType): VenueBooking[] => {
    const now = new Date();

    return bookings.filter((booking) => {
      const bookingDate = new Date(`${booking.bookingDate}T${booking.startTime}`);

      switch (tab) {
        case 'upcoming':
          return (
            (booking.status === 'PENDING' || booking.status === 'CONFIRMED') &&
            bookingDate >= now
          );
        case 'past':
          return booking.status === 'COMPLETED' || bookingDate < now;
        case 'cancelled':
          return booking.status === 'CANCELLED' || booking.status === 'NO_SHOW';
        default:
          return false;
      }
    });
  };

  const filteredBookings = filterBookings(bookings, activeTab);

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    padding: '32px',
    maxWidth: '1200px',
    margin: '0 auto',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '12px',
    fontFamily: theme.fonts.heading,
    letterSpacing: theme.fonts.letterSpacingWide,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
    marginBottom: '32px',
  };

  const tabsContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginBottom: '32px',
    borderBottom: `2px solid ${theme.colors.border}`,
    paddingBottom: '0',
  };

  const getTabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: isActive ? theme.colors.primary : theme.colors.textSecondary,
    border: 'none',
    borderBottom: `3px solid ${isActive ? theme.colors.primary : 'transparent'}`,
    fontSize: theme.fonts.sizeBody,
    fontWeight: isActive ? theme.fonts.weightBold : theme.fonts.weightNormal,
    cursor: 'pointer',
    transition: 'all 0.2s',
  });

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    fontSize: theme.fonts.sizeSubtitle,
    color: theme.colors.textSecondary,
  };

  const errorStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `2px solid ${theme.colors.danger}`,
  };

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
  };

  const emptyIconStyle: React.CSSProperties = {
    marginBottom: '16px',
    color: theme.colors.textSecondary,
  };

  const emptyTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
  };

  const emptyMessageStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  // Cancel Modal Styles
  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '32px',
    maxWidth: '500px',
    width: '100%',
    margin: '20px',
    border: `2px solid ${theme.colors.danger}`,
  };

  const modalTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const modalMessageStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
    marginBottom: '24px',
    lineHeight: '1.5',
  };

  const modalActionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  };

  const buttonBaseStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
  };

  const confirmButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: theme.colors.danger,
    color: theme.colors.text,
  };

  const getEmptyMessage = (tab: TabType): string => {
    switch (tab) {
      case 'upcoming':
        return 'No tenés reservas próximas. Buscá una cancha y reservá ahora!';
      case 'past':
        return 'No tenés reservas pasadas aún';
      case 'cancelled':
        return 'No tenés reservas canceladas';
      default:
        return 'No hay reservas';
    }
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <h1 style={titleStyle}>MIS RESERVAS</h1>
      <p style={subtitleStyle}>Gestioná tus reservas de canchas</p>

      {/* Tabs */}
      <div style={tabsContainerStyle}>
        <button
          style={getTabStyle(activeTab === 'upcoming')}
          onClick={() => setActiveTab('upcoming')}
          onMouseEnter={(e) => {
            if (activeTab !== 'upcoming') {
              e.currentTarget.style.color = theme.colors.text;
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'upcoming') {
              e.currentTarget.style.color = theme.colors.textSecondary;
            }
          }}
        >
          Próximas
        </button>
        <button
          style={getTabStyle(activeTab === 'past')}
          onClick={() => setActiveTab('past')}
          onMouseEnter={(e) => {
            if (activeTab !== 'past') {
              e.currentTarget.style.color = theme.colors.text;
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'past') {
              e.currentTarget.style.color = theme.colors.textSecondary;
            }
          }}
        >
          Pasadas
        </button>
        <button
          style={getTabStyle(activeTab === 'cancelled')}
          onClick={() => setActiveTab('cancelled')}
          onMouseEnter={(e) => {
            if (activeTab !== 'cancelled') {
              e.currentTarget.style.color = theme.colors.text;
            }
          }}
          onMouseLeave={(e) => {
            if (activeTab !== 'cancelled') {
              e.currentTarget.style.color = theme.colors.textSecondary;
            }
          }}
        >
          Canceladas
        </button>
      </div>

      {/* Loading State */}
      {loading && <div style={loadingStyle}>Cargando reservas...</div>}

      {/* Error State */}
      {error && !loading && (
        <div style={errorStyle}>
          <div style={{ color: theme.colors.danger, marginBottom: '8px' }}>
            Error al cargar reservas
          </div>
          <div>{error}</div>
        </div>
      )}

      {/* Bookings List */}
      {!loading && !error && (
        <>
          {filteredBookings.length === 0 ? (
            <div style={emptyStateStyle}>
              <div style={emptyIconStyle}>
                <Calendar size={64} />
              </div>
              <div style={emptyTitleStyle}>No hay reservas</div>
              <div style={emptyMessageStyle}>{getEmptyMessage(activeTab)}</div>
            </div>
          ) : (
            filteredBookings.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                onCancel={handleCancelClick}
                onCreateMatch={handleCreateMatch}
              />
            ))
          )}
        </>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div style={modalOverlayStyle} onClick={() => setShowCancelModal(false)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h2 style={modalTitleStyle}>
              <AlertCircle size={24} color={theme.colors.danger} />
              Cancelar Reserva
            </h2>
            <p style={modalMessageStyle}>
              ¿Estás seguro que querés cancelar esta reserva? Esta acción no se puede
              deshacer y podrías perder el depósito según las políticas de cancelación.
            </p>
            <div style={modalActionsStyle}>
              <button
                style={cancelButtonStyle}
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellingId(null);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.background;
                }}
              >
                No, volver
              </button>
              <button
                style={confirmButtonStyle}
                onClick={handleConfirmCancel}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#FF0033';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.danger;
                }}
              >
                Sí, cancelar reserva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyBookingsPage;
